import FontIcon from 'components/icons/FontIcon';
import React from 'react';
import {
  useCopyProductInformation,
  useCreateEpdProductInformation,
  useDeleteProductInformation,
  useUpdateEpdProductInformation,
} from 'services/api/mutations';
import { useEPDDefinitionInformation, useEpdProductInformation } from 'services/api/queries';
import styled from 'styled-components';
import {
  ExpandablePanel,
  ExpandablePanelHeaderName,
  FieldPanelFullWidth,
  HeaderCaptionSemiBold,
  IconsSection,
  PanelContainer,
} from 'styles/v2/Styles.styled';
import { EPDProductInformationModel } from 'types/types';

import { IconButton } from '../buttons';
import { AddCustomBlockButton } from '../buttons/AddCustomBlockButton';
import { confirmDelete } from '../confirm-dialog/confirmDelete';
import { StyledCopyIcon } from '../epd-compiler-inputs/common-icons';
import TooltipErrorIcon from '../icons/TooltipErrorIcon';
import { HelpBox, HelpBoxBoldText } from '../help-boxes';
import { EPDProductClassificationId, YES_NO } from './constants';
import useIsReadOnlyMode from './hooks/useIsReadOnlyMode';
import ToggleButton from './panels/toggleable/ToggleButton';
import ProductDataSection from './sections/productInformation/ProductDataSection';
import SkeletonProductInformationTab from './skeletons/SkeletonProductInformationTab';
import { usePcrV2Loading } from './hooks/usePcrV2';

const ProductInformationTab: React.FC<{
  epdId: string;
  epdVersionId: string;
  epdClassification: number | null | undefined;
  validationState: any;
}> = ({ epdId, epdVersionId, epdClassification, validationState }) => {
  const productInformation = useEpdProductInformation(epdVersionId).data;
  const epdDefinition = useEPDDefinitionInformation(epdVersionId).data;
  const updateMutation = useUpdateEpdProductInformation(epdVersionId);
  const createMutation = useCreateEpdProductInformation(epdVersionId);
  const deleteMutation = useDeleteProductInformation();
  const copyProductMutation = useCopyProductInformation();
  const isReadOnly = useIsReadOnlyMode();
  const isPcrV2Loading = usePcrV2Loading();

  const isLoading =
    epdClassification === undefined ||
    productInformation === undefined ||
    epdDefinition === undefined ||
    isPcrV2Loading === undefined ||
    validationState === undefined;

  if (isLoading) {
    return <SkeletonProductInformationTab />;
  }

  const confirmDeleteProduct = (product: EPDProductInformationModel) => {
    const productName = product.productName ?? 'Unnamed product';
    confirmDelete({
      caption: `Are you sure you want to delete product ${productName}?`,
      onConfirm: () => deleteMutation.mutate(product.id as string),
    });
  };

  const onChangeEpdProductInformation = async (propertyName: string, val: any, productId: string, versionId: string) => {
    updateMutation.mutate({ productId: productId, versionId: versionId, propertyName: propertyName, newValue: val });
  };

  const addProduct = () => {
    createMutation.mutate();
  };

  const handleCopyProduct = (productId: string | undefined) => {
    if (productId) {
      copyProductMutation.mutate({ epdVersionId, id: productId });
    }
  };

  const productErrors = (validationState: any, index: number) => {
    return validationState.errors[`products[${index}]`];
  };

  const hasMultipleProducts = productInformation.length > 1;

  const headerTemplate = (options: any, product: EPDProductInformationModel, errors: any) => {
    return (
      <div className={`${options.className} ${options.collapsed ? 'header-collapsed' : ''}`}>
        <ExpandablePanelHeaderName>
          <HeaderCaptionSemiBold>{product.productName ?? `Unnamed product`}</HeaderCaptionSemiBold>
          {errors && <TooltipErrorIcon />}
          <ToggleButton options={options} />
        </ExpandablePanelHeaderName>
        {!isReadOnly && (
          <IconsSection>
            {productInformation && productInformation.length > 1 && (
              <DeleteProductIconButton
                icon={<FontIcon icon="delete" style={{ fontSize: '0.9rem' }} />}
                text
                onClick={() => confirmDeleteProduct(product)}
              />
            )}
            <StyledCopyIcon onClick={() => handleCopyProduct(product.id)} />
          </IconsSection>
        )}
      </div>
    );
  };

  return (
    <>
      {epdClassification && epdClassification !== EPDProductClassificationId.SINGLE_PRODUCT_ID ? (
        <>
          {hasMultipleProducts && (
            <FieldPanelFullWidth>
              <HelpBox>
                <span>
                  The <strong>first product</strong> information is represented on the ECO Platform (ILCD+EPD format).
                </span>
              </HelpBox>
            </FieldPanelFullWidth>
          )}
          {productInformation.map((product: EPDProductInformationModel, index: number) => (
            <ExpandablePanelMarginContainer key={product?.id}>
              <ExpandablePanel
                className="multi-container multi-container-head"
                key={product?.id}
                headerTemplate={(options) => headerTemplate(options, product, productErrors(validationState, index))}
                toggleable
                collapsed
              >
                <ProductDataSection
                  product={product}
                  epdId={epdId}
                  epdVersionId={epdVersionId}
                  onChange={onChangeEpdProductInformation}
                  errors={productErrors(validationState, index)}
                  requirePublicationOnOkobaudat={epdDefinition?.requirePublicationOnOkobaudat === YES_NO.YES}
                />
              </ExpandablePanel>
            </ExpandablePanelMarginContainer>
          ))}
          {!isReadOnly && <AddCustomBlockButton caption="Add product" onClick={addProduct} />}
        </>
      ) : (
        <ExpandablePanel className="multi-container">
          <ProductDataSection
            product={productInformation[0]}
            epdId={epdId}
            epdVersionId={epdVersionId}
            onChange={onChangeEpdProductInformation}
            errors={productErrors(validationState, 0)}
            requirePublicationOnOkobaudat={epdDefinition?.requirePublicationOnOkobaudat === YES_NO.YES}
          />
        </ExpandablePanel>
      )}
    </>
  );
};

const ExpandablePanelMarginContainer = styled(PanelContainer)`
  box-shadow: none;
`;

export const DeleteProductIconButton = styled(IconButton)`
  width: 16px !important;
  height: 16px !important;
  &.p-button.p-button-text {
    color: ${(props) => props.theme.colors.primaryBlack};
  }
  &.p-button.p-button-text:hover {
    background: transparent;
  }
`;

export default ProductInformationTab;
